<!--
  @component AccountScreen — the consumer's account / profile page.
  tier:      screen (account-level — `/account`)
  reads:     useIdentityIO   → user + projects
  effects:   identity.signOut()

  Real API token CRUD lives in AccountTokensPanel mounted under
  TokensIO (the deployment IO portal). The mock list / mint /
  revoke flow is gone — every action dispatches `tokens.*` against
  the network deployment server.
-->
<script lang="ts">
  import { push } from "svelte-spa-router";
  import { useIdentityIO } from "./IdentityIO.svelte";
  import GuardedActionModal from "../../../global/visuals/primitives/GuardedActionModal.svelte";
  import TokensIO from "../../../global/io/nodes/deployment/TokensIO.svelte";
  import AccountTokensPanel from "./AccountTokensPanel.svelte";

  const identity = useIdentityIO();

  // ── Profile (local edit state) ─────────────────────────────
  let editDisplayName = $state(false);
  let displayNameDraft = $state("");
  function startEdit(): void {
    displayNameDraft = identity.user?.displayName ?? "";
    editDisplayName = true;
  }
  function cancelEdit(): void { editDisplayName = false; }
  function saveEdit(): void {
    // IdentityIO has no updateProfile in C0 — security adapter
    // hook point. Today the edit just closes; value resets on reload.
    editDisplayName = false;
  }

  let signOutOpen = $state(false);
  async function doSignOut(): Promise<void> {
    await identity.signOut();
    signOutOpen = false;
    push("/");
  }

  function timeAgo(ms?: number): string {
    if (!ms) return "—";
    const diff = Date.now() - ms;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  }
</script>

{#if !identity.user}
  <div class="notfound">
    <h1>Not signed in</h1>
    <p>Go back to <a href="#/">sign in</a>.</p>
  </div>
{:else}
  <div class="screen">
    <header class="hero">
      <h1>Account</h1>
      <p class="meta">
        <code>{identity.user.email}</code>
        · {identity.projects.length} project{identity.projects.length === 1 ? "" : "s"}
        · member since {timeAgo(identity.user.createdAtMs)}
      </p>
    </header>

    <!-- ── Profile rail ──────────────────────────────────────── -->
    <section class="rail">
      <header><h2>Profile</h2></header>
      <dl class="profile-dl">
        <div class="row">
          <dt>display name</dt>
          <dd>
            {#if editDisplayName}
              <input type="text" bind:value={displayNameDraft} spellcheck="false" />
              <button type="button" class="link" onclick={saveEdit}>save</button>
              <button type="button" class="link-muted" onclick={cancelEdit}>cancel</button>
            {:else}
              <span>{identity.user.displayName}</span>
              <button type="button" class="link" onclick={startEdit}>edit</button>
            {/if}
          </dd>
        </div>
        <div class="row">
          <dt>email</dt>
          <dd><code>{identity.user.email}</code></dd>
        </div>
        <div class="row">
          <dt>user id</dt>
          <dd><code class="muted">{identity.user.id}</code></dd>
        </div>
        <div class="row">
          <dt>created</dt>
          <dd>{timeAgo(identity.user.createdAtMs)}</dd>
        </div>
      </dl>
      <p class="caveat">
        Display-name editing isn't persisted yet — saves stay local
        until you reload. The hookup arrives with the upcoming
        multi-device session management.
      </p>
    </section>

    <!-- ── API tokens rail ───────────────────────────────────── -->
    <TokensIO>
      <AccountTokensPanel />
    </TokensIO>

    <!-- ── Sessions rail ─────────────────────────────────────── -->
    <section class="rail">
      <header><h2>Sessions</h2></header>
      <div class="session-row">
        <div>
          <strong>Current session</strong>
          <p>signed in as <code>{identity.user.email}</code> on this device</p>
        </div>
        <button type="button" class="destructive" onclick={() => (signOutOpen = true)}>
          sign out
        </button>
      </div>
      <p class="caveat">
        Multi-device session list + remote sign-out coming with the
        next auth pass.
      </p>
    </section>
  </div>
{/if}

<GuardedActionModal
  open={signOutOpen}
  title="Sign out"
  body="Sign out of this device. Your projects and tokens stay attached to your account — sign back in to pick up where you left off."
  confirmLabel="sign out"
  onConfirm={() => void doSignOut()}
  onCancel={() => (signOutOpen = false)}
/>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    font-family: var(--type-font);
    color: var(--text-primary);
  }
  .hero {
    padding-bottom: var(--space-4);
    border-bottom: 0.5px solid var(--border-subtle);
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
  }
  h1 { margin: 0; font-size: 22px; font-weight: 600; }
  .meta { margin: 0; font-size: 12px; color: var(--text-tertiary); }
  .meta code { font-family: var(--type-mono); font-size: 11px; color: var(--text-secondary); }

  .rail {
    padding: var(--space-4);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .rail header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
  }
  h2 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .caveat {
    margin: 0;
    font-size: 11px;
    color: var(--text-tertiary);
    line-height: 1.55;
  }
  /* Profile */
  .profile-dl {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .profile-dl .row {
    display: flex;
    gap: var(--space-4);
    align-items: baseline;
  }
  dt {
    flex: 0 0 120px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-tertiary);
  }
  dd {
    margin: 0;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  dd code { font-family: var(--type-mono); font-size: 12px; color: var(--text-primary); }
  dd code.muted { color: var(--text-tertiary); }
  dd input {
    padding: var(--space-1, 4px) var(--space-2);
    border-radius: var(--radius-sm);
    border: 0.5px solid var(--border-input);
    background: var(--surface-sunken);
    color: var(--text-primary);
    font: inherit;
    font-size: 12px;
  }
  dd input:focus { outline: none; border-color: var(--border-focus); }
  .link, .link-muted {
    appearance: none;
    background: transparent;
    border: 0;
    font: inherit;
    font-size: 11px;
    cursor: pointer;
    padding: 0;
  }
  .link { color: var(--accent-text); }
  .link:hover { text-decoration: underline; }
  .link-muted { color: var(--text-tertiary); }
  .link-muted:hover { color: var(--text-secondary); }

  /* Sessions */
  .session-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
  }
  .session-row strong { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .session-row p { margin: var(--space-1, 4px) 0 0; font-size: 12px; color: var(--text-secondary); }
  .session-row p code { font-family: var(--type-mono); font-size: 11px; color: var(--accent-text); }
  .destructive {
    appearance: none;
    background: var(--error-subtle);
    border: 0.5px solid var(--error);
    color: var(--error);
    border-radius: var(--radius-sm);
    padding: var(--space-2) var(--space-4);
    font: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  .destructive:hover { background: var(--error); color: var(--surface-base); }

  .notfound {
    max-width: 480px;
    margin: var(--space-8) auto;
    padding: var(--space-6);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-lg);
    text-align: center;
    font-family: var(--type-font);
  }
  .notfound h1 { margin: 0 0 var(--space-3); font-size: 18px; font-weight: 600; }
  .notfound p  { margin: 0; font-size: 13px; color: var(--text-secondary); line-height: 1.55; }
  .notfound a  { color: var(--accent-text); }
</style>
